import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { apiClient, ApiError } from '../services/api';
import { Project, Graph, GraphData } from '../types/persistence';

interface PersistenceState {
  // Auth
  isAuthenticated: boolean;
  
  // Projects
  currentProject: Project | null;
  projects: Project[];
  
  // Graphs
  currentGraph: Graph | null;
  graphs: Graph[];
  
  // State
  isLoading: boolean;
  error: string | null;
  lastSaved: Date | null;
  isDirty: boolean;
  
  // Auth actions
  authenticate: () => Promise<void>;
  logout: () => void;
  
  // Project actions
  loadProjects: () => Promise<void>;
  createProject: (name: string, description?: string) => Promise<Project>;
  selectProject: (projectId: string) => Promise<void>;
  updateProject: (projectId: string, updates: { name?: string; description?: string }) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  
  // Graph actions
  loadGraphs: () => Promise<void>;
  createGraph: (name: string, graphData?: GraphData) => Promise<Graph>;
  loadGraph: (graphId: string) => Promise<Graph>;
  saveGraph: (graphData: GraphData, changeDescription?: string) => Promise<void>;
  deleteGraph: (graphId: string) => Promise<void>;
  
  // State actions
  setDirty: (isDirty: boolean) => void;
  clearError: () => void;
}

export const usePersistenceStore = create<PersistenceState>()(
  devtools(
    (set, get) => ({
      // Initial state
      isAuthenticated: false,
      currentProject: null,
      projects: [],
      currentGraph: null,
      graphs: [],
      isLoading: false,
      error: null,
      lastSaved: null,
      isDirty: false,

      // Auth actions
      authenticate: async () => {
        set({ isLoading: true, error: null });
        try {
          // In development, get a dev token
          if (process.env.NODE_ENV === 'development') {
            const { token } = await apiClient.getDevToken();
            apiClient.setToken(token);
            set({ isAuthenticated: true });
            await get().loadProjects();
          }
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Authentication failed',
            isAuthenticated: false 
          });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      logout: () => {
        apiClient.setToken(null);
        set({
          isAuthenticated: false,
          currentProject: null,
          projects: [],
          currentGraph: null,
          graphs: [],
          lastSaved: null,
          isDirty: false
        });
      },

      // Project actions
      loadProjects: async () => {
        set({ isLoading: true, error: null });
        try {
          const { projects } = await apiClient.getProjects();
          set({ projects });
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to load projects' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      createProject: async (name: string, description?: string) => {
        set({ isLoading: true, error: null });
        try {
          const { project } = await apiClient.createProject({ name, description });
          set(state => ({ 
            projects: [...state.projects, project],
            currentProject: project
          }));
          return project;
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to create project' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      selectProject: async (projectId: string) => {
        set({ isLoading: true, error: null });
        try {
          const { project } = await apiClient.getProject(projectId);
          set({ currentProject: project });
          await get().loadGraphs();
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to select project' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateProject: async (projectId: string, updates: { name?: string; description?: string }) => {
        set({ isLoading: true, error: null });
        try {
          const { project } = await apiClient.updateProject(projectId, updates);
          set(state => ({
            projects: state.projects.map(p => p.id === projectId ? project : p),
            currentProject: state.currentProject?.id === projectId ? project : state.currentProject
          }));
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to update project' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      deleteProject: async (projectId: string) => {
        set({ isLoading: true, error: null });
        try {
          await apiClient.deleteProject(projectId);
          set(state => ({
            projects: state.projects.filter(p => p.id !== projectId),
            currentProject: state.currentProject?.id === projectId ? null : state.currentProject,
            graphs: state.currentProject?.id === projectId ? [] : state.graphs,
            currentGraph: state.currentProject?.id === projectId ? null : state.currentGraph
          }));
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to delete project' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Graph actions
      loadGraphs: async () => {
        const { currentProject } = get();
        if (!currentProject) return;

        set({ isLoading: true, error: null });
        try {
          const { graphs } = await apiClient.getGraphsByProject(currentProject.id);
          set({ graphs });
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to load graphs' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      createGraph: async (name: string, graphData?: GraphData) => {
        const { currentProject } = get();
        if (!currentProject) throw new Error('No project selected');

        set({ isLoading: true, error: null });
        try {
          // If no graph data provided, create empty graph
          const data = graphData || {
            nodes: [],
            edges: [],
            metadata: {}
          };

          const { graph } = await apiClient.createGraph(currentProject.id, { name, data });
          set(state => ({
            graphs: [...state.graphs, graph],
            currentGraph: graph,
            lastSaved: new Date(),
            isDirty: false
          }));
          return graph;
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to create graph' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      loadGraph: async (graphId: string) => {
        set({ isLoading: true, error: null });
        try {
          const { graph } = await apiClient.getGraph(graphId);
          set({ currentGraph: graph, lastSaved: new Date(graph.updated_at), isDirty: false });
          
          // Return the graph so the caller can update dialogue store
          return graph;
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to load graph' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      saveGraph: async (graphData: GraphData, changeDescription?: string) => {
        const { currentGraph } = get();
        if (!currentGraph) throw new Error('No graph selected');

        set({ isLoading: true, error: null });
        try {

          const { graph } = await apiClient.updateGraph(currentGraph.id, {
            data: graphData,
            change_description: changeDescription
          });
          
          set({ 
            currentGraph: graph, 
            lastSaved: new Date(),
            isDirty: false,
            graphs: get().graphs.map(g => g.id === graph.id ? graph : g)
          });
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to save graph' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      deleteGraph: async (graphId: string) => {
        set({ isLoading: true, error: null });
        try {
          await apiClient.deleteGraph(graphId);
          set(state => ({
            graphs: state.graphs.filter(g => g.id !== graphId),
            currentGraph: state.currentGraph?.id === graphId ? null : state.currentGraph
          }));
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to delete graph' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // State actions
      setDirty: (isDirty: boolean) => set({ isDirty }),
      clearError: () => set({ error: null })
    }),
    {
      name: 'persistence-store',
    }
  )
);